"use client";

import { Command as CommandPrimitive } from "cmdk";
import { MapPin, X } from "lucide-react";
import * as React from "react";
import { memo, type MouseEvent } from "react";

import { Command, CommandList } from "@/components/ui/command";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface LocationDropdownViewProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  placeholder: string;
  dataTestId?: string;
  searchTerm: string;
  suggestions: React.ReactNode;
  onSearchTermChange: (value: string) => void;
  clearSearch: (evt: MouseEvent<HTMLButtonElement>) => void;
  isMobile: boolean;
}

interface TriggerProps {
  placeholder: string;
  searchTerm: string;
  setOpen: (open: boolean) => void;
  onSearchTermChange: (value: string) => void;
  clearSearch: (evt: MouseEvent<HTMLButtonElement>) => void;
  dataTestId?: string;
  hasEvents: boolean;
}

const getTrigger = ({
  placeholder,
  searchTerm,
  setOpen,
  onSearchTermChange,
  clearSearch,
  dataTestId,
  hasEvents,
}: TriggerProps) => {
  const inputProps = {
    placeholder: placeholder,
    value: searchTerm,
    onFocus: () => setOpen(true),
    className: cn(
      "w-full justify-between h-9 min-w-0 text-sm rounded-md border border-border bg-background hover:border-line-strong transition-colors duration-150 text-foreground font-normal",
      "pl-9 pr-8",
    ),
    "data-testid": dataTestId,
  };

  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      {hasEvents ? (
        <CommandPrimitive.Input
          {...inputProps}
          onValueChange={onSearchTermChange}
        />
      ) : (
        <input {...inputProps} readOnly />
      )}
      {searchTerm && (
        <button
          type="button"
          onClick={clearSearch}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-full w-7 flex justify-center items-center text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Clear</span>
        </button>
      )}
    </div>
  );
};

const DesktopLocationDropdownView = ({
  open,
  setOpen,
  placeholder,
  searchTerm,
  suggestions,
  onSearchTermChange,
  clearSearch,
  dataTestId,
}: Omit<LocationDropdownViewProps, "isMobile">) => {
  return (
    <Command shouldFilter={false} className="overflow-visible">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor>
          {getTrigger({
            placeholder,
            searchTerm,
            setOpen,
            onSearchTermChange,
            clearSearch,
            dataTestId,
            hasEvents: true,
          })}
        </PopoverAnchor>
        {open && (
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()} // Prevent focus stealing/jumping
          >
            <CommandList className="max-h-[500px] overflow-y-auto">
              {suggestions}
            </CommandList>
          </PopoverContent>
        )}
      </Popover>
    </Command>
  );
};

const MobileLocationDropdownView = ({
  open,
  setOpen,
  placeholder,
  searchTerm,
  suggestions,
  onSearchTermChange,
  clearSearch,
  dataTestId,
}: Omit<LocationDropdownViewProps, "isMobile">) => {
  return (
    <Command shouldFilter={false} className="overflow-visible">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {getTrigger({
            placeholder,
            searchTerm,
            setOpen,
            onSearchTermChange,
            clearSearch,
            dataTestId,
            hasEvents: false,
          })}
        </SheetTrigger>
        <SheetContent side="bottom" className="w-full max-h-full top-0">
          <SheetTitle className="sr-only">Search</SheetTitle>
          <div className="mt-10 max-h-full mx-2">
            {getTrigger({
              placeholder,
              searchTerm,
              setOpen,
              onSearchTermChange,
              clearSearch,
              dataTestId,
              hasEvents: true,
            })}
            <CommandList className="max-h-full">{suggestions}</CommandList>
          </div>
        </SheetContent>
      </Sheet>
    </Command>
  );
};

export const LocationDropdownView = memo(
  ({
    open,
    setOpen,
    placeholder,
    searchTerm,
    suggestions,
    onSearchTermChange,
    clearSearch,
    dataTestId,
    isMobile,
  }: LocationDropdownViewProps) => {
    if (isMobile) {
      return (
        <MobileLocationDropdownView
          open={open}
          setOpen={setOpen}
          placeholder={placeholder}
          searchTerm={searchTerm}
          suggestions={suggestions}
          onSearchTermChange={onSearchTermChange}
          clearSearch={clearSearch}
          dataTestId={dataTestId}
        />
      );
    }

    return (
      <DesktopLocationDropdownView
        open={open}
        setOpen={setOpen}
        placeholder={placeholder}
        searchTerm={searchTerm}
        suggestions={suggestions}
        onSearchTermChange={onSearchTermChange}
        clearSearch={clearSearch}
        dataTestId={dataTestId}
      />
    );
  },
);

LocationDropdownView.displayName = "LocationDropdownView";
